// -----------------------------------------------------------------------------
// ProfileList.jsx – filtres + résultats + pagination (complet)
// -----------------------------------------------------------------------------
import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  Snackbar,
  Alert as MuiAlert,
  Tooltip,
  Divider,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import {
  Search as SearchIcon,
  ViewModule as BoxIcon,
  TableRows as TableIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
  History as HistoryIcon,
  ArrowBackIos as ArrowBackIosIcon,
  ArrowForwardIos as ArrowForwardIosIcon,
  ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";

import ProfileCard from "./ProfileCard";
import ProfileTable from "./ProfileTable";
import ProfileDetails from "./ProfileDetails";
import LocalisationAutocomplete from "./LocalisationAutocomplete";
import CustomLoader from "../Common/CustomLoader";

import {
  fetchProfiles,
  setCurrentPage,
  resetPagination,
  setSearchPerformed,
} from "../../redux/profilesSlice";
import {
  setSearchTerm,
  setLocalisation,
  setCompetences,
  setCertifications,
  setFormations,
  setCompetencesLogicOperator,
  setCertificationsLogicOperator,
  setFormationsLogicOperator,
  resetFilters,
  setInterExp,
} from "../../redux/filtersSlice";
import { fetchCurrentUser } from "../../redux/usersSlice";
import { saveSearch } from "../../services/apiService";
import { useSelector, useDispatch } from "react-redux";
import NoResults from "../Common/NoResults ";

const PRIMARY   = "#2F4E8B";
const HOVER     = "#4a6bad";
const FILTER_BG = "#f6f8fc";

/* ---------- Toggle ET / OU ---------- */
const LogicToggle = ({ value, onToggle }) => (
  <ToggleButtonGroup
    size="small"
    exclusive
    value={value}
    onChange={(_, v) => v && onToggle(v)}
    sx={{
      ml: 1,
      height: 28,
      "& .MuiToggleButton-root": {
        px: 1.5,
        fontSize: 12,
        fontWeight: 700,
        borderRadius: 1,
        borderColor: PRIMARY,
        "&.Mui-selected": {
          bgcolor: PRIMARY,
          color: "#fff",
          "&:hover": { bgcolor: HOVER },
        },
      },
    }}
  >
    <ToggleButton value="AND">ET</ToggleButton>
    <ToggleButton value="OR">OU</ToggleButton>
  </ToggleButtonGroup>
);

/* ---------- Grid qui reste stable (évite la perte de focus) ---------- */
const FiltersGrid = React.memo(({ children }) => (
  <Box
    sx={{
      display: "grid",
      gap: 2,
      gridTemplateColumns: {
        xs: "1fr",
        sm: "repeat(auto-fit, minmax(260px, 1fr))",
      },
    }}
  >
    {children}
  </Box>
));

export default function ProfileList() {
  const dispatch = useDispatch();
  const {
    profiles,
    currentPage,
    loading,
    error,
    searchPerformed,
  } = useSelector((s) => s.profiles);
  const f = useSelector((s) => s.filters);

  /* ---------------- local state ---------------- */
  const [filtersOpen, setFiltersOpen] = useState(true);
  const [viewMode, setViewMode]       = useState("card");
  const [snackbar, setSnackbar]       = useState({ open: false, msg: "", sev: "success" });
  const [selectedProfile, setSelectedProfile] = useState(null);

  // Add a ref for the results container
  const resultsRef = React.useRef(null);

  /* ------------- masquer filtres dans la fiche ------------- */
  useEffect(() => setFiltersOpen(!selectedProfile), [selectedProfile]);

  /* ---------------- pagination ---------------- */
  const PER       = 10;
  const total     = Math.ceil(profiles.length / PER);
  const pageSlice = profiles.slice((currentPage - 1) * PER, currentPage * PER);

  /* ---------------- effects ---------------- */
  useEffect(() => {
    if (searchPerformed && profiles.length === 0) dispatch(fetchProfiles());
  }, [dispatch, searchPerformed, profiles.length]);

  useEffect(() => { dispatch(fetchCurrentUser()); }, [dispatch]);

  /* ---------------- handlers ---------------- */
  const pageTo   = (p) => {
    if (p > 0 && p <= total) {
      dispatch(setCurrentPage(p));
      // Scroll to results when changing page
      setTimeout(() => {
        if (resultsRef.current) {
          resultsRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 0);
    }
  };
  const doSearch = () => {
    dispatch(resetPagination());
    dispatch(fetchProfiles());
    dispatch(setSearchPerformed(true));
  };
  const doClear  = () => { dispatch(resetFilters()); dispatch(resetPagination()); };
  const doSave   = async () => {
    try {
      await saveSearch({ filters: f, results: profiles });
      setSnackbar({ open: true, msg: "Recherche sauvegardée !", sev: "success" });
    } catch {
      setSnackbar({ open: true, msg: "Échec de la sauvegarde", sev: "error" });
    }
  };

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: 2,
      bgcolor: "#fff",
      "& fieldset": { borderColor: PRIMARY, borderWidth: 2 },
      "&:hover fieldset": { borderColor: HOVER },
      "&.Mui-focused fieldset": { borderColor: HOVER },
    },
  };

  /* ------------ rendu ------------ */
  return (
    <Box>
      {/* ================= FILTRES ================= */}
      {!selectedProfile && (
        <>
          {/* Barre titre + flèche */}
          <Box
            sx={{
              bgcolor: "#f1f3f5",
              borderBottom: "1px solid rgba(0,0,0,0.1)",
              px: 2,
              py: 1.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              cursor: "pointer",
            }}
            onClick={() => setFiltersOpen((o) => !o)}
          >
            <Typography variant="subtitle1" fontWeight={600}>
              Filtres de recherche
            </Typography>
            <ExpandMoreIcon
              sx={{
                transform: filtersOpen ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform .2s",
              }}
            />
          </Box>

          {/* Panneau filtres */}
          {filtersOpen && (
            <Box
              bgcolor={FILTER_BG}
              borderRadius={2}
              p={3}
              maxWidth={1280}
              mx="auto"
              mb={4}
              boxShadow="0 1px 3px rgba(0,0,0,.05)"
            >
              <Typography variant="h6" fontWeight={700} mb={2}>
                Filtres de recherche
              </Typography>

              <FiltersGrid>
                {/* Poste */}
                <TextField
                  placeholder="Poste : développeur"
                  value={f.searchTerm}
                  onChange={(e) => dispatch(setSearchTerm(e.target.value))}
                  fullWidth
                  variant="outlined"
                  sx={inputSx}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: PRIMARY }} />
                      </InputAdornment>
                    ),
                  }}
                />

                {/* Compétences */}
                <TextField
                  label="Compétences(virgules)"
                  placeholder="php, linux"
                  value={f.competences}
                  onChange={(e) => dispatch(setCompetences(e.target.value))}
                  fullWidth
                  variant="outlined"
                  sx={inputSx}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <LogicToggle
                          value={f.competencesLogicOperator}
                          onToggle={(v) => dispatch(setCompetencesLogicOperator(v))}
                        />
                      </InputAdornment>
                    ),
                  }}
                />

                {/* Formations */}
                <TextField
                  label="Formations(virgules)"
                  placeholder="Master, doctorat"
                  value={f.formations}
                  onChange={(e) => dispatch(setFormations(e.target.value))}
                  fullWidth
                  variant="outlined"
                  sx={inputSx}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <LogicToggle
                          value={f.formationsLogicOperator}
                          onToggle={(v) => dispatch(setFormationsLogicOperator(v))}
                        />
                      </InputAdornment>
                    ),
                  }}
                />

                {/* Certifications */}
                <TextField
                  label="Certifications(virgules)"
                  placeholder="CCNA, CCNP"
                  value={f.certifications}
                  onChange={(e) => dispatch(setCertifications(e.target.value))}
                  fullWidth
                  variant="outlined"
                  sx={inputSx}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <LogicToggle
                          value={f.certificationsLogicOperator}
                          onToggle={(v) => dispatch(setCertificationsLogicOperator(v))}
                        />
                      </InputAdornment>
                    ),
                  }}
                />

                {/* Localisation */}
                <LocalisationAutocomplete
                  value={f.localisation}
                  onChange={(v) => dispatch(setLocalisation(v))}
                  fullWidth
                  sx={inputSx}
                />

                {/* Expérience */}
                <FormControl fullWidth sx={inputSx}>
                  <InputLabel>Années d’expérience</InputLabel>
                  <Select
                    value={f.interExp}
                    label="Années d’expérience"
                    onChange={(e) => dispatch(setInterExp(e.target.value))}
                  >
                    <MenuItem value="">Toutes</MenuItem>
                    <MenuItem value="0-2">0-2 ans</MenuItem>
                    <MenuItem value="2-5">2-5 ans</MenuItem>
                    <MenuItem value="5-10">5-10 ans</MenuItem>
                    <MenuItem value="10+">10+ ans</MenuItem>
                  </Select>
                </FormControl>

                {/* Boutons actions */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Tooltip title="Rechercher">
                    <IconButton
                      onClick={doSearch}
                      disabled={loading || (!f.searchTerm && !f.competences)}
                      sx={{ color: loading || (!f.searchTerm && !f.competences) ? "grey.500" : PRIMARY }}
                    >
                      <SearchIcon sx={{ fontSize: "2rem" }} />
                    </IconButton>
                  </Tooltip>

                  <Tooltip title="Sauvegarder la recherche">
                    <IconButton
                      onClick={doSave}
                      disabled={loading || profiles.length === 0}
                      sx={{ color: loading || profiles.length === 0 ? "grey.500" : PRIMARY }}
                    >
                      <SaveIcon sx={{ fontSize: "2rem" }} />
                    </IconButton>
                  </Tooltip>

                  <Tooltip title="Réinitialiser les filtres">
                    <IconButton
                      onClick={doClear}
                      disabled={loading}
                      sx={{ color: loading ? "grey.500" : PRIMARY }}
                    >
                      <RefreshIcon sx={{ fontSize: "2rem" }} />
                    </IconButton>
                  </Tooltip>

                  <Box display="flex" alignItems="center" gap={0.5}>
                    <Tooltip title="Historique">
                      <IconButton
                        onClick={() =>
                          window.dispatchEvent(
                            new CustomEvent("navigateToComponent", {
                              detail: { component: "searchManagement" },
                            })
                          )
                        }
                      >
                        <HistoryIcon sx={{ color: PRIMARY, fontSize: "2.5rem" }} />
                      </IconButton>
                    </Tooltip>
                    <Typography
                      onClick={() =>
                        window.dispatchEvent(
                          new CustomEvent("navigateToComponent", {
                            detail: { component: "searchManagement" },
                          })
                        )
                      }
                      sx={{
                        color: PRIMARY,
                        fontStyle: "italic",
                        textDecoration: "underline",
                        fontSize: "0.875rem",
                        cursor: "pointer",
                      }}
                    >
                      Historique
                    </Typography>
                  </Box>
                </Box>
              </FiltersGrid>

              <Divider sx={{ my: 3 }} />
            </Box>
          )}
        </>
      )}

      {/* ================= BARRE RÉSULTATS ================= */}
      {searchPerformed && (
        <Box
          ref={resultsRef}
          sx={{
            bgcolor: "#fff",
            borderRadius: 2,
            boxShadow: "0 1px 3px rgba(0,0,0,.1)",
            px: 2,
            py: 1.5,
            mb: 3,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box display="flex" alignItems="center" gap={1}>
            {selectedProfile && (
              <IconButton size="small" onClick={() => setSelectedProfile(null)}>
                <ArrowBackIosIcon fontSize="small" />
              </IconButton>
            )}
            <Typography variant="subtitle1" fontWeight={700}>
              Résultats de recherche
            </Typography>
          </Box>

          {!selectedProfile && (
            <Box display="flex" alignItems="center" gap={1}>
              <Tooltip title="Affichage carte">
                <IconButton
                  size="small"
                  onClick={() => setViewMode("card")}
                  sx={{
                    bgcolor: viewMode === "card" ? PRIMARY : "transparent",
                    color : viewMode === "card" ? "#fff"   : "inherit",
                  }}
                >
                  <BoxIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Affichage tableau">
                <IconButton
                  size="small"
                  onClick={() => setViewMode("table")}
                  sx={{
                    bgcolor: viewMode === "table" ? PRIMARY : "transparent",
                    color : viewMode === "table" ? "#fff"   : "inherit",
                  }}
                >
                  <TableIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </Box>
      )}

      {/* ================= ZONE RÉSULTATS ================= */}
      {loading && <CustomLoader />}

      {error && !loading && (
        <Typography color="error" align="center" mt={4}>
          {error}
        </Typography>
      )}

      {searchPerformed && !loading && pageSlice.length === 0 && <NoResults />}

      {selectedProfile && !loading && <ProfileDetails profile={selectedProfile} />}

      {!selectedProfile &&
        searchPerformed &&
        !loading &&
        pageSlice.length > 0 && (
          <Box mt={3}>
            {viewMode === "card" ? (
              /* --------- Vue carte --------- */
              (() => {
                const teamPageSlice  = pageSlice.filter((p) => p.isTeam);
                const otherPageSlice = pageSlice.filter((p) => !p.isTeam);

                return (
                  <>
                    {/* Profils équipe */}
                    {teamPageSlice.length > 0 && (
                      <Box mb={4}>
                        <Typography variant="h6" sx={{ mb: 2 }}>
                          Vos profils
                        </Typography>
                        <Box display="grid" gridTemplateColumns="repeat(auto-fill,minmax(280px,1fr))" gap={2} pl={2}>
                          {teamPageSlice.map((p) => (
                            <ProfileCard key={p.id} profile={p} onClick={() => setSelectedProfile(p)} />
                          ))}
                        </Box>
                      </Box>
                    )}

                    {/* Autres profils */}
                    {otherPageSlice.length > 0 && (
                      <Box>
                        <Typography variant="h6" sx={{ mb: 2 }}>
                          Autres profils
                        </Typography>
                        <Box display="grid" gridTemplateColumns="repeat(auto-fill,minmax(280px,1fr))" gap={2} pl={2}>
                          {otherPageSlice.map((p) => (
                            <ProfileCard key={p.id} profile={p} onClick={() => setSelectedProfile(p)} />
                          ))}
                        </Box>
                      </Box>
                    )}
                  </>
                );
              })()
            ) : (
              /* --------- Vue tableau --------- */
              <ProfileTable rows={pageSlice} onSelect={setSelectedProfile} />
            )}
          </Box>
        )}

      {/* ================= PAGINATION ================= */}
      {!selectedProfile && profiles.length > PER && (
        <Box display="flex" justifyContent="center" alignItems="center" mt={4} gap={2}>
          <IconButton onClick={() => pageTo(currentPage - 1)} disabled={currentPage === 1}>
            <ArrowBackIosIcon sx={{ color: PRIMARY }} />
          </IconButton>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              bgcolor: PRIMARY,
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: 700,
            }}
          >
            {currentPage}
          </Box>
          <IconButton onClick={() => pageTo(currentPage + 1)} disabled={currentPage === total}>
            <ArrowForwardIosIcon sx={{ color: PRIMARY }} />
          </IconButton>
        </Box>
      )}

      {/* ================= SNACKBAR ================= */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <MuiAlert elevation={6} severity={snackbar.sev} sx={{ mb: 2 }}>
          {snackbar.msg}
        </MuiAlert>
      </Snackbar>
    </Box>
  );
}
